import { money } from '../../../shared/stash'
import type { Summary } from '../../lib/stash'
import Icon from '../Icon'

/** The scoreboard: level, streak, today's money, the week, the badges. */
export default function Home({ s, name, onGo }: { s: Summary; name: string; onGo: (tab: 'sell' | 'stash') => void }) {
  const peak = Math.max(1, ...s.week.map((d) => d.cents))
  const toNext = s.level.next !== null ? s.level.next - s.xp : 0
  const earned = s.badges.filter((b) => b.earned).length

  return (
    <div className="home">
      <section className="hero">
        <div className="hero-top">
          <div>
            <div className="hero-hi">Yo, {name}</div>
            <div className="hero-level">
              <span className="lvl-num">LV {s.level.index + 1}</span>
              <span className="lvl-title">{s.level.title}</span>
            </div>
          </div>
          <div className={`flame ${s.streak.current > 0 ? 'lit' : ''}`} title="Days in a row with a sale">
            <Icon name="flame" size={22} className="flame-icon" />
            <b>{s.streak.current}</b>
            <small>{s.streak.current === 1 ? 'day' : 'days'}</small>
          </div>
        </div>
        <div className="xp-bar"><span style={{ width: `${Math.max(3, Math.round(s.level.progress * 100))}%` }} /></div>
        <div className="xp-line">
          <span>{s.xp.toLocaleString()} XP</span>
          <span>{s.level.next !== null ? `${toNext.toLocaleString()} to next level` : 'Max level'}</span>
        </div>
        {s.streak.current > 0 && !s.streak.soldToday && (
          <div className="nudge">Make a sale today to keep your {s.streak.current}-day streak alive</div>
        )}
      </section>

      <section className="today">
        <div className="today-label">Today</div>
        <div className="today-num">{money(s.today.cents)}</div>
        <div className="today-sub">{s.today.count} {s.today.count === 1 ? 'sale' : 'sales'} · {money(s.weekCents)} this week</div>
        <div className="week" aria-label="Sales, last 7 days">
          {s.week.map((d, i) => (
            <div key={d.day} className={`week-col ${i === 6 ? 'is-today' : ''}`} title={`${d.day}: ${money(d.cents)}`}>
              <div className="week-bar"><span style={{ height: `${d.cents ? Math.max(6, (d.cents / peak) * 100) : 0}%` }} /></div>
              <small>{new Date(`${d.day}T12:00:00`).toLocaleDateString('en-US', { weekday: 'narrow' })}</small>
            </div>
          ))}
        </div>
      </section>

      <section className="tiles">
        <button type="button" className="tile" onClick={() => onGo('sell')}>
          <small>On the shelf</small>
          <b>{s.shelf.count}</b>
          <span>{money(s.shelf.cents)} if it all sells</span>
        </button>
        <div className="tile">
          <small>Profit</small>
          <b className={s.profitCents >= 0 ? 'up' : 'down'}>{money(s.profitCents)}</b>
          <span>{money(s.revenueCents)} sold, all time</span>
        </div>
      </section>

      {s.shelf.count === 0 && (
        <button type="button" className="cta" onClick={() => onGo('stash')}>
          <Icon name="box" /> {s.badges.find((b) => b.key === 'first_stash')?.earned === false ? 'Log your first batch' : 'Bag up some more'}
        </button>
      )}

      <section className="badges">
        <h3>Badges <small>{earned}/{s.badges.length}</small></h3>
        <div className="badge-grid">
          {s.badges.map((b) => (
            <div key={b.key} className={`badge ${b.earned ? 'earned' : ''}`} title={b.hint}>
              <span className="badge-icon"><Icon name={b.earned ? b.icon : 'lock'} size={26} /></span>
              <b>{b.title}</b>
              <small>{b.hint}</small>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}
